import React, { ReactNode } from "react";
import styled from "styled-components";
import Text from "./Text";
import TooltipIcon from "./TooltipIcon";
import Heading from "./Heading";
import { themeProp } from "../theme";

interface MetricProps {
  label?: string;
  size?: string;
  active?: boolean;
  printMode?: boolean;
  children?: ReactNode;
  help?: string;
  tooltipColor?: string;
  "data-cy"?: string;
  [key: string]: any; // For spreading additional props
}

interface MetricValueProps {
  size?: string;
}

interface MetricLabelProps {
  printMode?: boolean;
}

interface MetricContainerProps {
  active?: boolean;
}

const Metric: React.FC<MetricProps> = ({
  label,
  size = "h4",
  active,
  printMode,
  children,
  help,
  tooltipColor,
  "data-cy": dataCy,
  ...props
}) => (
  <MetricContainer active={active || printMode} data-cy={dataCy}>
    {children && (
      <MetricValue size={size} {...props} id="metric-value">
        {children}
      </MetricValue>
    )}

    <MetricLabelContainer>
      <MetricLabel printMode={printMode} id="metric-label">
        {label}
      </MetricLabel>
      <TooltipIcon
        title={help}
        color={tooltipColor}
        dataCy={"metric-tooltip"}
        disabled={undefined}
        dataCyTooltip={undefined}
      />
    </MetricLabelContainer>
  </MetricContainer>
);

const MetricValue = styled(Heading).attrs<MetricValueProps>((props) => ({
  level: props.size
}))<MetricValueProps>`
  color: inherit;
  margin-bottom: 0;
  max-height: ${(props) => themeProp(`typography.${props.size}.lineHeight`)};
  overflow: hidden;

  & > * {
    font-size: ${(props) => themeProp(`typography.${props.size}.fontSize`)};

    & > * {
      font-size: ${(props) => themeProp(`typography.${props.size}.fontSize`)};
    }
  }
`;

const MetricLabelContainer = styled.div`
  line-height: 18px;
`;

const MetricLabel = styled(Text).attrs<MetricLabelProps>((props) => ({
  variant: props.printMode ? "body" : "smallBody"
}))<MetricLabelProps>`
  color: inherit;
  margin-right: 4px;
`;

const MetricContainer = styled.div<MetricContainerProps>`
  color: inherit;
  display: flex;
  flex-direction: column;

  &:last-child {
    margin-bottom: 0;
  }
`;

export default Metric;
